/** Conversation UI for an owned generated view. Opening runs the consent-aware opener.
 * @module apps/web/src/components/flux-agent/views/FluxViewCard
 */
import { useState } from 'react';
import { PanelsTopLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FluxAgentAction } from '@/lib/fluxAgentApi';
import { loadViewProtocol } from '@/lib/fluxViewBridge';
import { FluxViewOpener } from './FluxViewOpener';

/** A view card only opens after a user click; suggestions populate the composer, never send it. */
export function FluxViewCard({
  action,
  onSuggest,
}: {
  /** Completed action containing the owner-scoped view id. */
  action: FluxAgentAction;
  /** Move a user-selected suggestion into the composer. */
  onSuggest?: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-background/70 space-y-2 rounded-md border p-3 text-sm"
      data-testid="flux-view-card"
    >
      <div className="flex items-start gap-2">
        <PanelsTopLeft className="text-primary mt-0.5 h-4 w-4" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{action.title}</p>
          <p className="text-muted-foreground text-xs">{action.summary}</p>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onPointerEnter={() => void loadViewProtocol().catch(() => {})}
        onFocus={() => void loadViewProtocol().catch(() => {})}
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
      >
        {open ? 'Close view' : 'Open view'}
      </Button>
      {open && action.viewId && <FluxViewOpener viewId={action.viewId} onSuggest={onSuggest} />}
    </div>
  );
}
